import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, signal, WritableSignal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { finalize, tap } from 'rxjs';

import {
  DialogBase,
  TiimeButtonModule,
  TiimeDialogModule,
  TiimeProgressSpinnerModule,
  TiimeSnackbarService
} from 'tiime-material';

import { CompanyApiService } from '@api-services/company-api.service';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { BusinessUnit } from '@models/business-unit';
import { Company } from '@models/company';
import { BusinessUnitSelectComponent } from '@shared-selectors/business-unit-select/business-unit-select.component';

import { CreateDemoCompanyDialogData } from './create-demo-company-dialog-data';

@Component({
  selector: 'app-create-demo-company-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TiimeDialogModule,
    TiimeProgressSpinnerModule,
    BusinessUnitSelectComponent,
    TiimeButtonModule
  ],
  templateUrl: './create-demo-company-dialog.component.html',
  styleUrl: './create-demo-company-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateDemoCompanyDialogComponent extends DialogBase<CreateDemoCompanyDialogComponent> {
  protected readonly submitLoading: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly businessUnitCtrl: FormControl<BusinessUnit | null> = new FormControl<BusinessUnit | null>(
    null,
    Validators.required
  );

  get businessUnitCtrlErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.businessUnitCtrl, 'Le groupe est requis');
  }

  constructor(
    dialogRef: MatDialogRef<CreateDemoCompanyDialogComponent>,
    private companyApiService: CompanyApiService,
    private snackbarService: TiimeSnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: CreateDemoCompanyDialogData
  ) {
    super(dialogRef);
  }

  closeCreateDemoCompanyDialog(): void {
    if (this.submitLoading()) {
      return;
    }

    this.cancel();
  }

  createDemoCompany(): void {
    if (this.businessUnitCtrl.invalid) {
      this.businessUnitCtrl.markAsTouched();
      return;
    }

    this.submitLoading.set(true);

    this.companyApiService
      .createDemoCompany(this.businessUnitCtrl.value.id)
      .pipe(
        tap((createdCompany: Company) => {
          this.dialogRef.close(createdCompany);
          this.snackbarService.openSuccess('Le dossier a bien été ajouté');
        }),
        finalize(() => this.submitLoading.set(false))
      )
      .subscribe();
  }
}
